import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { CloseOutlined, CheckCircleOutline } from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';
import { SIGN_IN_WITH_EMAIL } from '../../../constants/routes';
import { AuthModel, useAuth } from '../../../features/Auth';
import { VerifyEmailSlice } from '../SignIn/slice';
import configSlice from '../../../redux/configSlice';
import { EXTERNAL_LINKS, STRINGS } from '../../../constants';

const TermsAndConditions = ({ nextStep, goToNamedStep }) => {
  const [termsChecked, setTermsChecked] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useAuth();

  const { terms: termsLink } = useSelector(
    configSlice.selectors.getLinksConfig
  );

  const handleChange = useCallback(
    event => {
      setTermsChecked(event.target.checked);
    },
    [setTermsChecked]
  );

  const handleBack = useCallback(() => {
    dispatch(VerifyEmailSlice.actions.clearAll());
    navigate(`${SIGN_IN_WITH_EMAIL}?step=enter`, { replace: true });
  }, [navigate, dispatch]);

  const handleNext = useCallback(async () => {
    if (!AuthModel.isDpdUserAccount(auth.currentSession)) {
      if (AuthModel.isP2GAccount(auth.currentSession)) {
        nextStep();
      } else {
        goToNamedStep('verifyPhone');
      }
    } else {
      navigate('/', { replace: true });
    }
  }, [nextStep, auth.currentSession]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant='h1' sx={visuallyHidden}>
        {STRINGS.TERMS_AND_CONDITIONS}
      </Typography>
      <Typography variant='h5' component='h2'>
        {STRINGS.HOW_WE_USE_YOUR_DATA}
      </Typography>
      <List>
        <ListItem>
          <ListItemIcon>
            <CheckCircleOutline color='success' />
          </ListItemIcon>
          <ListItemText primary={STRINGS.TO_KEEP_YOU_INFORMED} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <CheckCircleOutline color='success' />
          </ListItemIcon>
          <ListItemText primary={STRINGS.ENSURE_OUR_SERVICES} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <CheckCircleOutline color='success' />
          </ListItemIcon>
          <ListItemText primary={STRINGS.FOR_YOUR_PARCEL_PREFERENCES} />
        </ListItem>
      </List>
      <Typography variant='h5' component='h2'>
        {STRINGS.NEVER_USE_YOUR_DATA_TO}
      </Typography>
      <List>
        <ListItem>
          <ListItemIcon>
            <CloseOutlined color='error' />
          </ListItemIcon>
          <ListItemText primary={STRINGS.NEVER_SELL_YOUR_DATA} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <CloseOutlined color='error' />
          </ListItemIcon>
          <ListItemText primary={STRINGS.NO_KEEP_DATA_LONGER_THAN_NEEDED} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <CloseOutlined color='error' />
          </ListItemIcon>
          <ListItemText primary={STRINGS.NO_SEND_MARKETING_INFO} />
        </ListItem>
      </List>
      <Box>
        <Typography sx={{ display: 'inline' }}>Please see our </Typography>
        <Link
          href={EXTERNAL_LINKS.PRIVACY_POLICY_URL}
          underline='none'
          target='_blank'
          sx={{ display: 'inline' }}
        >
          Privacy Policy
        </Link>
        <Typography sx={{ display: 'inline' }}> and </Typography>
        <Link
          href={termsLink ?? '#'}
          underline='none'
          target='_blank'
          sx={{ display: 'inline' }}
        >
          {STRINGS.TERMS_AND_CONDITIONS}
        </Link>
        <Typography sx={{ display: 'inline' }}>
          {' '}
          for more details on how your personal data will be used.
        </Typography>
      </Box>
      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              id='terms-checkbox'
              checked={termsChecked}
              slotProps={{
                htmlInput: { 'aria-label': STRINGS.I_ACCEPT_TERMS },
              }}
              onChange={handleChange}
            />
          }
          label={STRINGS.I_ACCEPT_TERMS}
        />
      </Box>
      <Box
        sx={{
          mt: 4,
          alignSelf: 'flex-end',
        }}
      >
        <Button
          sx={{
            alignSelf: 'flex-end',
          }}
          variant='outlined'
          color='inherit'
          onClick={handleBack}
        >
          {STRINGS.BACK}
        </Button>
        <Button
          sx={{
            ml: 2,
            alignSelf: 'flex-end',
          }}
          type='submit'
          variant='contained'
          disabled={!termsChecked}
          onClick={handleNext}
        >
          {STRINGS.NEXT}
        </Button>
      </Box>
    </Box>
  );
};

export default TermsAndConditions;
